import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { getApiUrl } from '../utils/apiConfig';
import './AdminTimeTracking.css';

const AdminTimeTracking = ({ user }) => {
  const [timeLogs, setTimeLogs] = useState([]);
  const [pendingLogs, setPendingLogs] = useState([]);
  const [users, setUsers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [pendingLoading, setPendingLoading] = useState(true);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  const now = new Date();
  const defaultYear = now.getFullYear();
  const defaultMonth = now.getMonth() + 1; // 1-12
  const [filters, setFilters] = useState({
    // Month range selection (inclusive)
    startYear: defaultYear.toString(),
    startMonth: String(defaultMonth).padStart(2, '0'),
    endYear: defaultYear.toString(),
    endMonth: String(defaultMonth).padStart(2, '0'),
    // Other filters
    userType: '',
    userId: ''
  });
  const [activeTab, setActiveTab] = useState('all'); // 'all' or 'pending'
  const [exportLoading, setExportLoading] = useState(false);
  const [exportFormat, setExportFormat] = useState('pdf');

  useEffect(() => {
    fetchTimeLogs();
    fetchPendingLogs();
    fetchUsers();
  }, []);

  const fetchTimeLogs = async () => {
    try {
      setLoading(true);
      const apiUrl = await getApiUrl('/api/admin/time/logs');
      const response = await axios.get(apiUrl);
      setTimeLogs(response.data);
    } catch (error) {
      setError('Failed to load time logs');
      console.error('Error fetching time logs:', error);
    } finally {
      setLoading(false);
    }
  };

  const fetchPendingLogs = async () => {
    try {
      setPendingLoading(true);
      const apiUrl = await getApiUrl('/api/admin/time/pending');
      const response = await axios.get(apiUrl);
      setPendingLogs(response.data);
    } catch (error) {
      setError('Failed to load pending time logs');
      console.error('Error fetching pending time logs:', error);
    } finally {
      setPendingLoading(false);
    }
  };

  const fetchUsers = async () => {
    try {
      const apiUrl = await getApiUrl('/api/admin/users');
      const response = await axios.get(apiUrl);
      setUsers(response.data);
    } catch (error) {
      console.error('Error fetching users:', error);
    }
  };



  const formatDateTime = (dateTime) => {
    if (!dateTime) return '';
    // Backend already provides data in DD/MM/YYYY HH:MM:SS format
    return dateTime;
  };

  const getDateFromClockIn = (clockIn) => {
    if (!clockIn) return '';
    // Extract date part from DD/MM/YYYY HH:MM:SS format
    return clockIn.split(' ')[0];
  };



  // Convert decimal hours to HH:MM format
  const formatHoursToHHMM = (decimalHours) => {
    if (!decimalHours || decimalHours === 0) return '00:00';
    
    const totalMinutes = Math.round(decimalHours * 60);
    const hours = Math.floor(totalMinutes / 60);
    const minutes = totalMinutes % 60;
    
    return `${hours.toString().padStart(2, '0')}:${minutes.toString().padStart(2, '0')}`;
  };

  const handleApprove = async (logId) => {
    try {
      setError('');
      setSuccess('');
      await axios.post(`/api/admin/time/approve/${logId}`);
      setSuccess('Time entry approved successfully');
      fetchPendingLogs();
      fetchTimeLogs();
    } catch (error) {
      setError('Failed to approve time entry');
      console.error('Error approving time entry:', error);
    }
  };

  const handleReject = async (logId, rejectionReason) => {
    try {
      setError('');
      setSuccess('');
      await axios.post(`/api/admin/time/reject/${logId}`, {
        rejection_reason: rejectionReason
      });
      setSuccess('Time entry rejected successfully');
      fetchPendingLogs();
      fetchTimeLogs();
    } catch (error) {
      setError('Failed to reject time entry');
      console.error('Error rejecting time entry:', error);
    }
  };

  const getStatusBadge = (status) => {
    switch (status) {
      case 'approved':
        return <span className="status-badge approved">✓ Approved</span>;
      case 'pending':
        return <span className="status-badge pending">⏳ Pending</span>;
      case 'rejected':
        return <span className="status-badge rejected">✗ Rejected</span>;
      default:
        return <span className="status-badge approved">✓ Approved</span>;
    }
  };

  const monthKey = (yyyy, mm) => `${yyyy}${mm}`;
  const toMonthKeyFromBrazilDate = (ddmmyyyy) => {
    if (!ddmmyyyy) return '';
    const parts = ddmmyyyy.split('/');
    const yyyy = parts[2];
    const mm = parts[1];
    return `${yyyy}${mm}`;
  };

  const getFilteredLogs = () => {
    let filtered = timeLogs;

    // Month range filter (inclusive)
    const startKey = monthKey(filters.startYear, filters.startMonth);
    const endKey = monthKey(filters.endYear, filters.endMonth);
    if (startKey && endKey) {
      filtered = filtered.filter(log => {
        const logKey = toMonthKeyFromBrazilDate(log.date);
        return logKey >= startKey && logKey <= endKey;
      });
    }

    if (filters.userType) {
      filtered = filtered.filter(log => log.user_type === filters.userType);
    }

    if (filters.userId) {
      filtered = filtered.filter(log => log.user_id.toString() === filters.userId);
    }

    return filtered;
  };

  const getMonthRangeDates = () => {
    const startYearNum = parseInt(filters.startYear, 10);
    const startMonthNum = parseInt(filters.startMonth, 10);
    const endYearNum = parseInt(filters.endYear, 10);
    const endMonthNum = parseInt(filters.endMonth, 10);
    const startDate = `${startYearNum}-${String(startMonthNum).padStart(2, '0')}-01`;
    const lastDay = new Date(endYearNum, endMonthNum, 0).getDate();
    const endDate = `${endYearNum}-${String(endMonthNum).padStart(2, '0')}-${String(lastDay).padStart(2, '0')}`;
    return { startDate, endDate, valid: `${startYearNum}${String(startMonthNum).padStart(2, '0')}` <= `${endYearNum}${String(endMonthNum).padStart(2, '0')}` };
  };

  const handleExport = async () => {
    const { startDate, endDate, valid } = getMonthRangeDates();
    if (!valid) {
      setError('Start month must be before or equal to end month');
      return;
    }

    try {
      setExportLoading(true);
      setError('');
      
      const apiUrl = await getApiUrl(`/api/time-export/export/${exportFormat}`);
      const response = await axios.post(apiUrl, {
        userId: filters.userId || null, // null means all users for admin
        startDate,
        endDate
      }, {
        responseType: 'blob'
      });

      // Create download link
      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement('a');
      link.href = url;
      
      // Generate filename
      const startDateStr = startDate.replace(/-/g, '_');
      const endDateStr = endDate.replace(/-/g, '_');
      const userStr = filters.userId ? 
        users.find(u => u.id.toString() === filters.userId)?.name.replace(/\s+/g, '_').toLowerCase() || 'user' : 
        'allusers';
      
      link.setAttribute('download', `${userStr}_${startDateStr}_${endDateStr}.${exportFormat}`);
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);
      
      setSuccess(`Export completed successfully`);
    } catch (error) {
      setError('Failed to export time logs');
      console.error('Export error:', error);
    } finally {
      setExportLoading(false);
    }
  };

  const filteredLogs = getFilteredLogs();
  const monthRange = getMonthRangeDates();

  return (
    <div className="admin-time-tracking">
      <div className="admin-time-tracking-header">
        <h2>Time Tracking Administration</h2>
      </div>

      {error && (
        <div className="error-message">
          {error}
        </div>
      )}

      {success && (
        <div className="success-message">
          {success}
        </div>
      )}

      <div className="filters-section">
        <h3>Filters</h3>
        <div className="filters-grid">
          <div className="filter-group">
            <label>Start Month:</label>
            <div style={{ display: 'flex', gap: '0.5rem' }}>
              <select
                value={filters.startMonth}
                onChange={(e) => setFilters({ ...filters, startMonth: e.target.value })}
              >
                {Array.from({ length: 12 }, (_, i) => String(i + 1).padStart(2, '0')).map(m => (
                  <option key={m} value={m}>{m}</option>
                ))}
              </select>
              <select
                value={filters.startYear}
                onChange={(e) => setFilters({ ...filters, startYear: e.target.value })}
              >
                {Array.from({ length: 6 }, (_, i) => (defaultYear - i).toString()).map(y => (
                  <option key={y} value={y}>{y}</option>
                ))}
              </select>
            </div>
          </div>
          <div className="filter-group">
            <label>End Month:</label>
            <div style={{ display: 'flex', gap: '0.5rem' }}>
              <select
                value={filters.endMonth}
                onChange={(e) => setFilters({ ...filters, endMonth: e.target.value })}
              >
                {Array.from({ length: 12 }, (_, i) => String(i + 1).padStart(2, '0')).map(m => (
                  <option key={m} value={m}>{m}</option>
                ))}
              </select>
              <select
                value={filters.endYear}
                onChange={(e) => setFilters({ ...filters, endYear: e.target.value })}
              >
                {Array.from({ length: 6 }, (_, i) => (defaultYear - i).toString()).map(y => (
                  <option key={y} value={y}>{y}</option>
                ))}
              </select>
            </div>
          </div>
          <div className="filter-group">
            <label>Role:</label>
            <select
              value={filters.userType}
              onChange={(e) => setFilters({...filters, userType: e.target.value})}
            >
              <option value="">All Roles</option>
              <option value="director">Director</option>
              <option value="operator">Operator</option>
              <option value="actor">Actor</option>
            </select>
          </div>
          <div className="filter-group">
            <label>User:</label>
            <select
              value={filters.userId}
              onChange={(e) => setFilters({...filters, userId: e.target.value})}
            >
              <option value="">All Users</option>
              {users
                .filter(u => ['actor', 'director', 'operator'].includes(u.user_type))
                .map(user => (
                  <option key={user.id} value={user.id}>
                    {user.name} ({user.user_type})
                  </option>
                ))}
            </select>
          </div>
        </div>
      </div>

      <div className="export-section">
        <h3>Export Time Logs</h3>
        <div className="export-controls">
          <div className="export-format">
            <label>Format:</label>
            <select
              value={exportFormat}
              onChange={(e) => setExportFormat(e.target.value)}
            >
              <option value="pdf">PDF</option>
              <option value="csv">CSV</option>
            </select>
          </div>
          <div className="export-scope">
            <label>Scope:</label>
            <span className="export-info">
              {filters.userId ? 
                `Selected user: ${users.find(u => u.id.toString() === filters.userId)?.name || 'Unknown'}` : 
                'All users'
              }
            </span>
          </div>
          <button 
            className="export-btn"
            onClick={handleExport}
            disabled={exportLoading || !monthRange.valid}
          >
            {exportLoading ? 'Exporting...' : `Export ${exportFormat.toUpperCase()}`}
          </button>
        </div>
        {!monthRange.valid && (
          <div className="export-warning">
            Start month must be before or equal to end month
          </div>
        )}
      </div>

      <div className="tabs-section">
        <button 
          className={`tab-btn ${activeTab === 'all' ? 'active' : ''}`}
          onClick={() => setActiveTab('all')}
        >
          All Time Logs ({filteredLogs.length})
        </button>
        <button 
          className={`tab-btn ${activeTab === 'pending' ? 'active' : ''}`}
          onClick={() => setActiveTab('pending')}
        >
          Pending Approval ({pendingLogs.length})
        </button>
      </div>

      {activeTab === 'all' && (
        <div className="stats-section">
          <div className="stat-card">
            <h4>Total Records</h4>
            <span className="stat-value">{filteredLogs.length}</span>
          </div>
          <div className="stat-card">
            <h4>Total Hours</h4>
            <span className="stat-value">{formatHoursToHHMM(filteredLogs.reduce((total, log) => total + (log.total_hours || 0), 0))}</span>
          </div>
          <div className="stat-card">
            <h4>Active Sessions</h4>
            <span className="stat-value">
              {filteredLogs.filter(log => !log.clock_out).length}
            </span>
          </div>
        </div>
      )}

      <div className="time-logs-section">
        <h3>{activeTab === 'all' ? 'All Time Logs' : 'Pending Approval'}</h3>
        
        {activeTab === 'all' ? (
          loading ? (
            <div className="loading">Loading time logs...</div>
          ) : (
            <>
              <div className="time-logs-table">
                <table>
                  <thead>
                    <tr>
                      <th>Name</th>
                      <th>Role</th>
                      <th>Date</th>
                      <th>Clock In</th>
                      <th>Clock Out</th>
                      <th>Total Hours</th>
                      <th>Status</th>
                    </tr>
                  </thead>
                  <tbody>
                    {filteredLogs.map((log) => (
                      <tr key={log.id} className={log.clock_out ? 'completed' : 'active'}>
                        <td>{log.user_name}</td>
                        <td>
                          <span className={`role-badge ${log.user_type}`}>
                            {log.user_type}
                          </span>
                        </td>
                        <td>{getDateFromClockIn(log.clock_in)}</td>
                        <td>{formatDateTime(log.clock_in)}</td>
                        <td>{log.clock_out ? formatDateTime(log.clock_out) : 'Active'}</td>
                        <td>{log.total_hours ? formatHoursToHHMM(log.total_hours) : '-'}</td>
                        <td>{getStatusBadge(log.status)}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>

              {filteredLogs.length === 0 && (
                <div className="no-logs">
                  No time logs found matching the current filters.
                </div>
              )}
            </>
          )
        ) : (
          pendingLoading ? (
            <div className="loading">Loading pending time logs...</div>
          ) : (
            <>
              <div className="time-logs-table">
                <table>
                  <thead>
                    <tr>
                      <th>Name</th>
                      <th>Role</th>
                      <th>Date</th>
                      <th>Clock In</th>
                      <th>Clock Out</th>
                      <th>Total Hours</th>
                      <th>Submitted</th>
                      <th>Actions</th>
                    </tr>
                  </thead>
                  <tbody>
                    {pendingLogs.map((log) => (
                      <tr key={log.id} className="pending">
                        <td>{log.user_name}</td>
                        <td>
                          <span className={`role-badge ${log.user_type}`}>
                            {log.user_type}
                          </span>
                        </td>
                        <td>{getDateFromClockIn(log.clock_in)}</td>
                        <td>{formatDateTime(log.clock_in)}</td>
                        <td>{log.clock_out ? formatDateTime(log.clock_out) : 'Active'}</td>
                        <td>{log.total_hours ? formatHoursToHHMM(log.total_hours) : '-'}</td>
                        <td>{formatDateTime(log.submitted_at)}</td>
                        <td>
                          <div className="action-buttons">
                            <button
                              className="approve-btn"
                              onClick={() => handleApprove(log.id)}
                              title="Approve"
                            >
                              ✓
                            </button>
                            <button
                              className="reject-btn"
                              onClick={() => {
                                const reason = prompt('Enter rejection reason:');
                                if (reason) handleReject(log.id, reason);
                              }}
                              title="Reject"
                            >
                              ✗
                            </button>
                          </div>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>

              {pendingLogs.length === 0 && (
                <div className="no-logs">
                  No pending time logs found.
                </div>
              )}
            </>
          )
        )}
      </div>
    </div>
  );
};

export default AdminTimeTracking;
